import { useMemo, useState } from "react";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { useQueryGetTireModelsList } from "src/queries/administrator/tireModel.queries";
import type { TireModelRecord } from "src/services/administrator/tireModelService";
import { hasAdministrationPermission } from "src/util/administrationPermission";
import TireModelsTable from "./TireModelsTable";
import "../../Customers/Customers.css";
import TireModelFormModal from "./TireModelsFormModal";

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

interface TireModelSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

const TireModels = () => {
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<TireModelSorting>({
    sortBy: "ID",
    sortDir: "DESC",
  });
  const [search, setSearch] = useState("");
  const [formRecord, setFormRecord] = useState<TireModelRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const canEdit = hasAdministrationPermission("edit");

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sortDir ?? "DESC",
      sortBy: sorting.sortBy ?? "ID",
    }),
    [pagination.page, pagination.size, sorting.sortBy, sorting.sortDir]
  );

  const { data, isLoading } = useQueryGetTireModelsList(queryParams, {
    enabled: true,
  });

  const response = data?.data?.response;
  const records = useMemo(
    () =>
      response?.resources ??
      response?.tireModels ??
      response?.data ??
      response?.items ??
      response?.content ??
      [],
    [response]
  );

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return records;

    return records.filter((record) =>
      [record.name, record.description, record.active ? "active" : "inactive"]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [records, search]);

  const totalCount = search.trim()
    ? filteredRecords.length
    : (response?.totalItems ?? response?.totalElements ?? response?.total ?? records.length);

  const openCreateForm = () => {
    setFormRecord(null);
    setIsFormOpen(true);
  };

  const openEditForm = (record: TireModelRecord) => {
    setFormRecord(record);
    setIsFormOpen(true);
  };

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="tire_model"
        buttonKey="create"
        onClick={openCreateForm}
        showBtn={canEdit}
        buttonProps={{
          className: "!text-gray2 !w-[94px] !h-[40px]",
        }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <TireModelsTable
          data={filteredRecords}
          isLoading={isLoading}
          pagination={{ ...pagination, total: totalCount }}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          onEdit={openEditForm}
        />
      </div>

      <TireModelFormModal
        open={isFormOpen}
        record={formRecord}
        onClose={() => setIsFormOpen(false)}
      />
    </section>
  );
};

export default TireModels;
